import { useState, useCallback, useRef } from 'react';
import { spatialApi } from '../lib/api';
import { userFeatures } from '../lib/draw-features';
import { DRAW_MODE_MAP, DRAW_MODE_REVERSE } from '../lib/mapViewHelpers';

function applyMode(control, draw, mode, setActiveDrawMode) {
  if (!mode) {
    control.resetActiveMode();
    setActiveDrawMode(null);
  } else {
    control.activate();
    draw.setMode(DRAW_MODE_MAP[mode] || mode);
    setActiveDrawMode(mode);
  }
}

/** Terra Draw mode state and handlers for MapView. */
export function useDrawingTools({ setResultLayers }) {
  const [showDrawingPanel, setShowDrawingPanel] = useState(false);
  const [activeDrawMode, setActiveDrawMode] = useState(null);
  const [drawnFeatures, setDrawnFeatures] = useState([]);
  const drawControlRef = useRef(null);
  const pendingModeRef = useRef(null); // mode requested before the draw control was ready

  // ── Terra Draw wiring ──
  const handleDrawReady = useCallback((control) => {
    drawControlRef.current = control;
    const draw = control.getTerraDrawInstance();
    if (draw) {
      draw.on('change', () => {
        try { setDrawnFeatures(userFeatures(draw.getSnapshot())); } catch { /* store not ready */ }
      });
    }
    control.on('mode-changed', (e) => {
      setActiveDrawMode(DRAW_MODE_REVERSE[e.mode] || null);
    });
    if (draw && pendingModeRef.current) {
      const mode = pendingModeRef.current;
      pendingModeRef.current = null;
      try { applyMode(control, draw, mode, setActiveDrawMode); } catch { /* control not usable yet */ }
    }
  }, []);

  const setDrawMode = useCallback((mode) => {
    const control = drawControlRef.current;
    const draw = control?.getTerraDrawInstance();
    if (!draw) {
      // Map still loading: remember the request and apply it when the control is ready (E3)
      pendingModeRef.current = mode || null;
      if (mode) setActiveDrawMode(mode);
      else setActiveDrawMode(null);
      return;
    }
    applyMode(control, draw, mode, setActiveDrawMode);
  }, []);

  const undoLastDrawn = useCallback(() => {
    const draw = drawControlRef.current?.getTerraDrawInstance();
    if (!draw) return;
    const snap = userFeatures(draw.getSnapshot());
    const last = snap[snap.length - 1];
    if (last?.id !== undefined) {
      draw.removeFeatures([last.id]);
      setDrawnFeatures(userFeatures(draw.getSnapshot()));
    }
  }, []);

  const clearDrawn = useCallback(() => {
    const draw = drawControlRef.current?.getTerraDrawInstance();
    if (draw) draw.clear();
    setDrawnFeatures([]);
  }, []);

  const onDbPolygonSelect = () => {
    const polys = drawnFeatures.filter(f => ['Polygon', 'MultiPolygon'].includes(f.geometry?.type));
    const poly = polys[polys.length - 1];
    if (!poly) return;
    spatialApi.featuresInPolygon(poly.geometry)
      .then(res => {
        const fc = res.data;
        const n = fc?.features?.length || 0;
        if (n > 0) {
          setResultLayers(prev => [...prev, {
            id: `result-${Date.now()}`, name: `DB selection (${n} features)`,
            geojson: fc, kind: 'vector', visible: true,
          }]);
        } else {
          window.alert('No database features inside the polygon (tables may be empty, or migrations 009/010 not applied).');
        }
      })
      .catch(err => window.alert(`Polygon select failed: ${err.response?.data?.detail || err.message}`));
  };

  const exportDrawn = () => {
    const geojson = { type: 'FeatureCollection', features: userFeatures(drawnFeatures) };
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = 'drawn-features.geojson'; link.click();
    URL.revokeObjectURL(url);
  };

  const closeDrawingPanel = () => { setShowDrawingPanel(false); setDrawMode(null); };

  return {
    showDrawingPanel, setShowDrawingPanel, activeDrawMode, drawnFeatures,
    handleDrawReady, setDrawMode, undoLastDrawn, clearDrawn,
    onDbPolygonSelect, exportDrawn, closeDrawingPanel,
  };
}
